"use client"

import type React from "react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/auth-context"
import { useRouter, usePathname } from "@/lib/navigation"
import { ThemeToggle } from "@/components/theme-toggle"
import { SquaresFour as LayoutDashboard, Users, Gear as Settings, FileText, DeviceMobile as Smartphone, SignOut as LogOut, User, Shield, Gift, Scroll as ScrollText, FadersHorizontal as SlidersHorizontal, ShoppingCart, Cloud } from "@phosphor-icons/react"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void
}

export function Sidebar({ className, onClose }: SidebarProps) {
  const { userType, logout } = useAuth()
  const router = useRouter()
  const pathname = usePathname()

  const handleLogout = () => {
    logout()
    router.push("/")
  }

  const handleNavigation = (href: string) => {
    router.push(href)
    onClose?.()
  }

  const getMenuItems = () => {
    const baseItems = [
      {
        title: "总览",
        icon: LayoutDashboard,
        href: `/${userType}/dashboard`,
      },
    ]

    if (userType === "user") {
      return [
        ...baseItems,
        {
          title: "账户配置",
          icon: User,
          href: "/user/account",
        },
        {
          title: "我的日志",
          icon: FileText,
          href: "/user/logs",
        },
        {
          title: "其他设置",
          icon: SlidersHorizontal,
          href: "/user/settings",
        },
      ]
    }

    if (userType === "admin") {
      return [
        ...baseItems,
        {
          title: "用户管理",
          icon: Users,
          href: "/admin/users",
        },
        {
          title: "购买订单",
          icon: ShoppingCart,
          href: "/admin/orders",
        },
        {
          title: "任务管理",
          icon: Settings,
          href: "/admin/tasks",
        },
        {
          title: "设备管理",
          icon: Smartphone,
          href: "/admin/devices",
        },
        {
          title: "CDK管理",
          icon: Gift,
          href: "/admin/cdk",
        },
        {
          title: "上游代打",
          icon: Cloud,
          href: "/admin/upstream-ark",
        },
        {
          title: "日志管理",
          icon: ScrollText,
          href: "/admin/logs",
        },
        {
          title: "其他设置",
          icon: SlidersHorizontal,
          href: "/admin/settings",
        },
      ]
    }

    return baseItems
  }

  const menuItems = getMenuItems()

  return (
    <div className={cn("h-full flex flex-col", className)}>
      <div className="space-y-4 py-4 mb-0 flex-1 min-h-0 overflow-y-auto">
        <div className="px-3 py-2">
          <div className={cn("flex items-center justify-between mb-4", onClose && "pr-12")}>
            <div className="flex items-center gap-2">
              {userType === "admin" && <Shield className="w-5 h-5" style={{ color: "var(--berry-600)" }} />}
              {userType === "user" && <User className="w-5 h-5" style={{ color: "var(--berry-600)" }} />}
              <h2 className="text-lg font-semibold tracking-tight" style={{ color: "var(--berry-600)" }}>
                {userType === "admin" && "管理员面板"}
                {userType === "user" && "用户面板"}
              </h2>
            </div>
            <ThemeToggle />
          </div>
          <div className="space-y-1">
            {menuItems.map((item) => {
              const isActive = pathname === item.href
              return (
                <Button
                  key={item.href}
                  aria-current={isActive ? "page" : undefined}
                  variant={isActive ? "default" : "ghost"}
                  style={isActive ? { background: "var(--berry-300)", color: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.15)" } : { color: "var(--berry-600)", background: "transparent", border: "none" }}
                  className={cn(
                    "w-full justify-start transition-colors",
                    !isActive && "hover:bg-blush-300",
                  )}
                  onClick={() => handleNavigation(item.href)}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  {item.title}
                </Button>
              )
            })}
          </div>
        </div>
      </div>
      <div style={{ flexShrink: 0, padding: "8px 12px max(16px, env(safe-area-inset-bottom))", borderTop: "1px solid var(--berry-300)" }}>
        <Button
          variant="ghost"
          style={{ width: "100%", justifyContent: "flex-start", color: "var(--berry-300)", background: "transparent", border: "none" }}
          onClick={handleLogout}
          className="hover:bg-red-50"
        >
          <LogOut className="mr-2 h-4 w-4" />
          退出登录
        </Button>
      </div>
    </div>
  )
}
